import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Users, Calendar, DollarSign, TrendingUp, Building, Eye, LogOut, Settings } from 'lucide-react';
import apiService from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import WeddingAnniversariesWidget from '../components/WeddingAnniversariesWidget';

/**
 * Dashboard Zone - Version restaurée avec structure originale
 */
const ZoneDashboard = () => {
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const { user, logout, getModerateurRedirectUrl } = useAuth();

    // Fonction pour formater les montants avec K et M
    const formatAmount = (amount) => {
        if (!amount || amount === 0) return '0';
        const num = parseFloat(amount);
        
        if (num >= 1000000) {
            return (num / 1000000).toFixed(1).replace('.0', '') + 'M';
        } else if (num >= 1000) {
            return (num / 1000).toFixed(1).replace('.0', '') + 'K';
        }
        return num.toLocaleString();
    };

    const fetchDashboardData = useCallback(async () => {
        try {
            console.log('Récupération des données du dashboard zone...');
            console.log('Utilisateur connecté:', user);

            // Vérifier le token d'auth d'abord
            const token = sessionStorage.getItem('fsf_auth_token') || localStorage.getItem('fsf_auth_token');
            console.log('Token présent:', !!token);

            if (!token) {
                console.log('Aucun token - redirection vers login');
                navigate('/login');
                return;
            }

            console.log('Appel API vers /zone-dashboard...');
            
            // Vérifier les permissions de l'utilisateur avant l'appel
            if (!user?.zone) {
                console.warn('Utilisateur sans zone assignée:', user);
                setDashboardData({
                    zone_info: {
                        name: 'Zone non assignée',
                        description: 'Aucune zone assignée à cet utilisateur'
                    },
                    statistics: {
                        total_members: 0,
                        active_fraternities: 0,
                        recent_registrations: 0,
                        monthly_contributions: 0,
                        total_contributions: 0,
                        pending_approvals: 0,
                        collection_percentage: 0
                    },
                    fraternities: [],
                    recent_activities: [],
                    user_role: user?.role
                });
                return;
            }

            // Utiliser l'endpoint correct pour le dashboard zone
            const response = await apiService.get('/zone-dashboard');
            console.log('Réponse dashboard zone complète:', response);
            console.log('Type de réponse:', typeof response);
            console.log('Structure de la réponse:', Object.keys(response || {}));

            // Gérer différents formats de réponse
            const data = response.data || response;
            if (!data) {
                console.warn('Aucune donnée reçue, utilisation de données par défaut');
                // Données par défaut pour éviter l'erreur
                setDashboardData({
                    zone_info: {
                        name: user?.zone || 'Zone non définie',
                        description: 'Zone en cours de configuration'
                    },
                    statistics: {
                        total_members: 0,
                        active_fraternities: 0,
                        recent_registrations: 0,
                        monthly_contributions: 0,
                        total_contributions: 0,
                        pending_approvals: 0
                    },
                    fraternities: [],
                    recent_activities: []
                });
            } else {
                // Adapter les données de l'API au format attendu par le frontend
                const adaptedData = {
                    zone_info: {
                        name: data.zone || user?.zone || 'Zone non définie',
                        description: `Zone ${data.zone} - ${data.user_role?.toUpperCase()}`
                    },
                    statistics: {
                        total_members: data.zone_stats?.total_members || 0,
                        active_fraternities: data.zone_stats?.total_fraternities || 0,
                        recent_registrations: 0, // À implémenter si nécessaire
                        monthly_contributions: data.zone_stats?.monthly_contributions || 0,
                        total_contributions: data.zone_stats?.expected_monthly_amount || 0,
                        pending_approvals: 0, // À implémenter si nécessaire
                        collection_percentage: data.zone_stats?.collection_percentage || 0
                    },
                    fraternities: data.fraternities || [],
                    recent_activities: data.recent_contributions || [],
                    recent_contributions: data.recent_contributions || [],
                    user_role: data.user_role
                };

                console.log('Données adaptées:', adaptedData);
                console.log('Contributions récentes dans adaptedData:', adaptedData.recent_contributions);
                setDashboardData(adaptedData);
            }
        } catch (error) {
            console.error('=== ERREUR DASHBOARD ZONE ===');
            console.error('Erreur lors de la récupération des données du dashboard:', error);
            console.error('Détails erreur:', error.response);
            console.error('Status:', error.response?.status);
            console.error('Data:', error.response?.data);
            console.error('Message:', error.message);
            console.error('=============================');

            if (error.response?.status === 403) {
                console.log('Accès refusé - redirection vers dashboard principal');
                navigate('/dashboard');
            } else if (error.response?.status === 401) {
                console.log('Non authentifié - nettoyage du localStorage et redirection');
                sessionStorage.removeItem('fsf_auth_token'); localStorage.removeItem('fsf_auth_token');
                localStorage.removeItem('fsf_user');
                navigate('/login');
            } else {
                console.warn('Erreur de chargement, utilisation de données par défaut:', error.message);
                // Utiliser des données par défaut même en cas d'erreur
                setDashboardData({
                    zone_info: {
                        name: user?.zone || 'Zone non définie',
                        description: 'Erreur de chargement - Veuillez réessayer'
                    },
                    statistics: {
                        total_members: 0,
                        active_fraternities: 0,
                        recent_registrations: 0,
                        monthly_contributions: 0,
                        total_contributions: 0,
                        pending_approvals: 0
                    },
                    fraternities: [],
                    recent_activities: []
                });
            }
        } finally {
            setLoading(false);
        }
    }, [user, navigate]);

    useEffect(() => {
        // === CONTRÔLE ACCÈS MODÉRATEUR ===
        // Les modérateurs doivent être redirigés vers leur fraternité
        const moderateurRedirectUrl = getModerateurRedirectUrl();
        if (moderateurRedirectUrl) {
            console.log('🔐 Modérateur détecté - redirection vers:', moderateurRedirectUrl);
            navigate(moderateurRedirectUrl, { replace: true });
            return;
        }

        fetchDashboardData();
    }, [fetchDashboardData, getModerateurRedirectUrl, navigate]);



    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Chargement du dashboard zone...</p>
                </div>
            </div>
        );
    }

    // Extraction sécurisée des données avec valeurs par défaut
    const zoneInfo = dashboardData?.zone_info || { name: user?.zone || 'Zone non définie', description: 'Zone en cours de configuration' };
    const stats = dashboardData?.statistics || {};
    const fraternities = dashboardData?.fraternities || [];
    const recentContributions = dashboardData?.recent_contributions || [];

    // Debug pour voir ce qui est reçu
    console.log('=== DEBUG ZoneDashboard ===');
    console.log('dashboardData:', dashboardData);
    console.log('recentContributions:', recentContributions);
    console.log('recentContributions.length:', recentContributions.length);

    // Mapping des rôles pour l'affichage
    const roleDisplayNames = {
        'apz': 'Animateur Principal de Zone',
        'sgz': 'Secrétaire Général de Zone',
        'tgz': 'Trésorier Général de Zone',
        'mcz': 'Membre de Comité de Zone'
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header avec déconnexion */}
            <div className="bg-white shadow-sm border-b">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-14 sm:h-16">
                        {/* Logo et titre */}
                        <div className="flex items-center flex-1 min-w-0">
                            <div className="flex-shrink-0">
                                <h1 className="text-base sm:text-xl font-semibold text-gray-900 truncate">
                                    <span className="sm:hidden">FSF - {zoneInfo.name}</span>
                                    <span className="hidden sm:inline">FSF - Zone {zoneInfo.name}</span>
                                </h1>
                            </div>
                        </div>

                        {/* User menu */}
                        <div className="flex items-center space-x-2 sm:space-x-4">
                            <Link 
                                to="/profile"
                                className="p-1.5 sm:p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 transition-colors"
                                title="Profil utilisateur"
                            >
                                <Settings className="h-4 w-4 sm:h-5 sm:w-5" />
                            </Link>
                            <div className="flex items-center space-x-2 sm:space-x-3">
                                <div className="text-xs sm:text-sm text-right hidden sm:block">
                                    <p className="font-medium text-gray-900">{user?.first_name || 'Admin'}</p>
                                    <p className="text-gray-500 truncate max-w-32">{roleDisplayNames[user?.role] || user?.role}</p>
                                </div>
                                <div className="w-8 h-8 sm:hidden bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                                    {(user?.first_name || 'A').charAt(0).toUpperCase()}
                                </div>
                                <button
                                    onClick={handleLogout}
                                    className="flex items-center space-x-1 sm:space-x-2 bg-red-500 hover:bg-red-600 text-white px-2 py-1.5 sm:px-3 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-colors"
                                >
                                    <LogOut className="h-3 w-3 sm:h-4 sm:w-4" />
                                    <span className="hidden sm:inline">Déconnexion</span>
                                    <span className="sm:hidden">Exit</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Contenu principal */}
            <div className="p-4 sm:p-6 max-w-7xl mx-auto">
                {/* En-tête */}
                <div className="mb-6 sm:mb-8">
                    <h1 className="text-xl sm:text-3xl font-bold text-gray-800 mb-2">
                        <span className="sm:hidden">Zone {zoneInfo.name}</span>
                        <span className="hidden sm:inline">Dashboard Zone {zoneInfo.name}</span>
                    </h1>
                    <p className="text-sm sm:text-lg text-gray-600">
                        <span className="sm:hidden">{(roleDisplayNames[user?.role] || user?.role).replace('Animateur Principal de Zone', 'APZ').replace('Secrétaire Général de Zone', 'SGZ').replace('Trésorier Général de Zone', 'TGZ')}</span>
                        <span className="hidden sm:inline">{roleDisplayNames[user?.role] || user?.role}</span>
                    </p>
                </div>

                {/* Statistiques générales */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
                    <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
                        <div className="flex items-center">
                            <div className="p-2 sm:p-3 bg-blue-100 rounded-full">
                                <Users className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
                            </div>
                            <div className="ml-3 sm:ml-4">
                                <p className="text-xs sm:text-sm font-medium text-gray-600">Total Membres</p>
                                <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.total_members || 0}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
                        <div className="flex items-center">
                            <div className="p-2 sm:p-3 bg-green-100 rounded-full">
                                <Building className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
                            </div>
                            <div className="ml-3 sm:ml-4">
                                <p className="text-xs sm:text-sm font-medium text-gray-600">Fraternités</p>
                                <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.active_fraternities || 0}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
                        <div className="flex items-center">
                            <div className="p-2 sm:p-3 bg-yellow-100 rounded-full">
                                <DollarSign className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-600" />
                            </div>
                            <div className="ml-3 sm:ml-4 min-w-0">
                                <p className="text-xs sm:text-sm font-medium text-gray-600">Cotisations ce mois</p>
                                <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
                                    {formatAmount(stats.monthly_contributions || 0)} F
                                    <span className="hidden sm:inline"> CFA</span>
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
                        <div className="flex items-center">
                            <div className="p-2 sm:p-3 bg-purple-100 rounded-full">
                                <TrendingUp className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600" />
                            </div>
                            <div className="ml-3 sm:ml-4">
                                <p className="text-xs sm:text-sm font-medium text-gray-600">Taux de collecte</p>
                                <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.collection_percentage || 0}%</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Boutons d'actions rapides */}
                <div className="mb-6 sm:mb-8">
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3 sm:mb-4">Actions Rapides</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                        <Link
                            to="/zone-contributions"
                            className="bg-green-500 hover:bg-green-600 text-white p-3 sm:p-4 rounded-lg flex items-center justify-center space-x-2 transition-colors"
                        >
                            <DollarSign className="h-4 w-4 sm:h-5 sm:w-5" />
                            <span className="text-sm sm:text-base font-medium">
                                <span className="sm:hidden">Cotisations</span>
                                <span className="hidden sm:inline">Gérer les Cotisations</span>
                            </span>
                        </Link>

                        <Link
                            to="/zone-events"
                            className="bg-blue-500 hover:bg-blue-600 text-white p-3 sm:p-4 rounded-lg flex items-center justify-center space-x-2 transition-colors"
                        >
                            <Calendar className="h-4 w-4 sm:h-5 sm:w-5" />
                            <span className="text-sm sm:text-base font-medium">
                                <span className="sm:hidden">Événements</span>
                                <span className="hidden sm:inline">Gérer les Événements</span>
                            </span>
                        </Link>

                        <Link
                            to="/zone-members"
                            className="bg-purple-500 hover:bg-purple-600 text-white p-3 sm:p-4 rounded-lg flex items-center justify-center space-x-2 transition-colors"
                        >
                            <Users className="h-4 w-4 sm:h-5 sm:w-5" />
                            <span className="text-sm sm:text-base font-medium">
                                <span className="sm:hidden">Membres</span>
                                <span className="hidden sm:inline">Gérer les Membres</span>
                            </span>
                        </Link>
                    </div>
                </div>

                <WeddingAnniversariesWidget />

                {/* Fraternités de la zone */}
                <div className="mb-6 sm:mb-8">
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3 sm:mb-4">Fraternités de la Zone</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                        {fraternities.map((fraternity, index) => (
                            <Link
                                key={index}
                                to={`/fraternity/${encodeURIComponent(fraternity.name)}/members`}
                                className="bg-white p-4 sm:p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow border-l-4 border-blue-500 block"
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-semibold text-gray-800 mb-2 text-sm sm:text-base truncate">{fraternity.name}</h3>
                                        <div className="flex items-center text-gray-600">
                                            <Users className="h-3 w-3 sm:h-4 sm:w-4 mr-2 flex-shrink-0" />
                                            <span className="text-xs sm:text-sm">{fraternity.members_count} membre{fraternity.members_count > 1 ? 's' : ''}</span>
                                        </div>
                                    </div>
                                    <div className="ml-3 sm:ml-4 flex-shrink-0">
                                        <div className="bg-blue-100 p-1.5 sm:p-2 rounded-full">
                                            <Eye className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>

                    {fraternities.length === 0 && (
                        <div className="text-center py-6 sm:py-8 text-gray-500">
                            <Building className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-4 opacity-50" />
                            <p className="text-sm sm:text-base">Aucune fraternité trouvée dans cette zone</p>
                        </div>
                    )}
                </div>

                {/* Résumé financier détaillé */}
                <div className="mb-6 sm:mb-8">
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3 sm:mb-4">Résumé Financier</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
                        <div className="bg-gradient-to-r from-green-400 to-green-600 text-white p-3 sm:p-4 rounded-lg">
                            <h3 className="text-xs sm:text-sm font-medium opacity-90">Cotisations Collectées</h3>
                            <p className="text-lg sm:text-2xl font-bold">{formatAmount(stats.monthly_contributions || 0)} F</p>
                            <p className="text-xs sm:text-sm opacity-75">Ce mois-ci</p>
                        </div>
                        <div className="bg-gradient-to-r from-blue-400 to-blue-600 text-white p-3 sm:p-4 rounded-lg">
                            <h3 className="text-xs sm:text-sm font-medium opacity-90">Montant Attendu</h3>
                            <p className="text-lg sm:text-2xl font-bold">{formatAmount((stats.total_contributions || 0) / 12)} F</p>
                            <p className="text-xs sm:text-sm opacity-75">Objectif mensuel</p>
                        </div>
                        <div className="bg-gradient-to-r from-purple-400 to-purple-600 text-white p-3 sm:p-4 rounded-lg">
                            <h3 className="text-xs sm:text-sm font-medium opacity-90">Reste à Collecter</h3>
                            <p className="text-lg sm:text-2xl font-bold">{formatAmount(((stats.total_contributions || 0) / 12) - (stats.monthly_contributions || 0))} F</p>
                            <p className="text-xs sm:text-sm opacity-75">Pour ce mois</p>
                        </div>
                    </div>
                </div>

                {/* Cotisations récentes */}
                <div>
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-semibold text-gray-800">Cotisations Récentes</h2>
                        <Link 
                            to="/zone-contributions"
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                            Voir toutes les cotisations →
                        </Link>
                    </div>
                    <div className="bg-white rounded-lg shadow-md overflow-hidden text-justify">
                        {recentContributions.length > 0 ? (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Membre
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Fraternité
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Montant
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Statut
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Date
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {recentContributions.map((contribution) => (
                                            <tr key={contribution.id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                    {contribution.user?.first_name} {contribution.user?.last_name}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {contribution.user?.fraternity || 'Non assigné'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {formatAmount(contribution.amount || 0)} F CFA
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${contribution.status === 'paid'
                                                            ? 'bg-green-100 text-green-800'
                                                            : 'bg-yellow-100 text-yellow-800'
                                                        }`}>
                                                        {contribution.status === 'paid' ? 'Payé' : 'En attente'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {contribution.created_at ? new Date(contribution.created_at).toLocaleDateString() : 'N/A'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-center py-8 text-gray-500">
                                <DollarSign className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                <p>Aucune cotisation récente</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ZoneDashboard;